import React, { useState, useEffect } from 'react';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    TrashIcon,
    EyeIcon,
    FunnelIcon,
    BuildingOffice2Icon,
    UsersIcon,
    CurrencyDollarIcon,
    MapPinIcon,
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    DocumentArrowDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import { apiUrl } from '../../utils/api';

interface User {
    _id: string;
    nombre: string;
    apellido: string;
    email: string;
    tipoSocio: string;
}

interface Building {
    _id: string;
    nombre: string;
    direccion: string;
    ciudad: string;
}

interface Property {
    _id: string;
    numero: string;
    edificio?: Building;
    tipo: 'habitacion' | 'apartamento' | 'suite';
    descripcion: string;
    capacidad: number;
    precioBase: number;
    estado: 'disponible' | 'ocupada' | 'mantenimiento' | 'reservada';
    propietarios: Array<{
        usuario: User;
        porcentajePropiedad: number;
    }>;
    amenidades: string[];
    piso: number;
    activa: boolean;
}

interface PropertyFormData {
    numero: string;
    edificio: string;
    tipo: 'habitacion' | 'apartamento' | 'suite';
    descripcion: string;
    capacidad: number;
    precioBase: number;
    piso: number;
    amenidades: string[];
    estado: 'disponible' | 'ocupada' | 'mantenimiento' | 'reservada';
    propietarios: Array<{
        usuario: string;
        porcentajePropiedad: number;
    }>;
}

const PropertyManagement: React.FC = () => {
    const [properties, setProperties] = useState<Property[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [buildings, setBuildings] = useState<Building[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<string>('');
    const [filterEstado, setFilterEstado] = useState<string>('');
    const [showModal, setShowModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [editingProperty, setEditingProperty] = useState<Property | null>(null);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importResults, setImportResults] = useState<any>(null);
    const [formData, setFormData] = useState<PropertyFormData>({
        numero: '',
        edificio: '',
        tipo: 'habitacion',
        descripcion: '',
        capacidad: 1,
        precioBase: 0,
        piso: 1,
        amenidades: [],
        estado: 'disponible',
        propietarios: []
    });

    const [pagination, setPagination] = useState({
        current: 1,
        pages: 1,
        total: 0
    });

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchProperties(1);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [filterType, filterEstado, searchTerm]);

    useEffect(() => {
        fetchUsers();
        fetchBuildings();
    }, []);

    const fetchProperties = async (page = 1) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            params.append('page', page.toString());
            params.append('limit', '10');
            if (filterType) params.append('tipo', filterType);
            if (filterEstado) params.append('estado', filterEstado);
            if (searchTerm) params.append('search', searchTerm);

            const response = await fetch(apiUrl(`/api/admin/properties?${params}`), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setProperties(data.data.properties);
                if (data.data.pagination) {
                    setPagination(data.data.pagination);
                }
            } else {
                console.error('Error al obtener propiedades');
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            // Request a large limit to ensure all users are loaded for the dropdown
            const response = await fetch(apiUrl('/api/admin/users?limit=1000&activo=true'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setUsers(data.data.users);
            }
        } catch (error) {
            console.error('Error al obtener usuarios:', error);
        }
    };

    const fetchBuildings = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/buildings'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setBuildings(data.data.buildings);
            } else {
                console.error('Error al obtener edificios');
            }
        } catch (error) {
            console.error('Error al obtener edificios:', error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validar que se asignen propietarios
        if (formData.propietarios.length === 0) {
            alert('Debe asignar al menos un propietario a la propiedad');
            return;
        }

        // Validar que los porcentajes sumen 100%
        if (getTotalPorcentaje() !== 100) {
            alert('Los porcentajes de propiedad deben sumar exactamente 100%');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const url = editingProperty ? apiUrl(`/api/admin/properties/${editingProperty._id}`) : apiUrl('/api/admin/properties');
            const method = editingProperty ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                await fetchProperties(pagination.current);
                setShowModal(false);
                setEditingProperty(null);
                setFormData({
                    numero: '',
                    edificio: '',
                    tipo: 'habitacion',
                    descripcion: '',
                    capacidad: 1,
                    precioBase: 0,
                    piso: 1,
                    amenidades: [],
                    estado: 'disponible',
                    propietarios: []
                });
            } else {
                const error = await response.json();
                alert(error.message || 'Error al guardar propiedad');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al guardar propiedad');
        }
    };

    const handleEdit = (property: Property) => {
        setEditingProperty(property);
        setFormData({
            numero: property.numero,
            edificio: property.edificio?._id || '',
            tipo: property.tipo,
            descripcion: property.descripcion,
            capacidad: property.capacidad,
            precioBase: property.precioBase,
            piso: property.piso,
            amenidades: property.amenidades,
            estado: property.estado,
            propietarios: property.propietarios.map(p => ({
                usuario: p.usuario._id,
                porcentajePropiedad: p.porcentajePropiedad
            }))
        });
        setShowModal(true);
    };

    const addPropietario = () => {
        setFormData({
            ...formData,
            propietarios: [...formData.propietarios, { usuario: '', porcentajePropiedad: 0 }]
        });
    };

    const removePropietario = (index: number) => {
        setFormData({
            ...formData,
            propietarios: formData.propietarios.filter((_, i) => i !== index)
        });
    };

    const updatePropietario = (index: number, field: 'usuario' | 'porcentajePropiedad', value: string | number) => {
        const newPropietarios = [...formData.propietarios];
        newPropietarios[index] = { ...newPropietarios[index], [field]: value };
        setFormData({ ...formData, propietarios: newPropietarios });
    };

    const getTotalPorcentaje = () => {
        return formData.propietarios.reduce((sum, p) => sum + p.porcentajePropiedad, 0);
    };

    const getTipoLabel = (tipo: string) => {
        const labels: { [key: string]: string } = {
            'habitacion': 'Habitación',
            'apartamento': 'Apartamento',
            'suite': 'Suite'
        };
        return labels[tipo] || tipo;
    };

    const getEstadoLabel = (estado: string) => {
        const labels: { [key: string]: string } = {
            'disponible': 'Disponible',
            'ocupada': 'Ocupada',
            'mantenimiento': 'Mantenimiento',
            'reservada': 'Reservada'
        };
        return labels[estado] || estado;
    };

    const getEstadoColor = (estado: string) => {
        const colors: { [key: string]: string } = {
            'disponible': 'bg-green-100 text-green-800',
            'ocupada': 'bg-red-100 text-red-800',
            'mantenimiento': 'bg-yellow-100 text-yellow-800',
            'reservada': 'bg-blue-100 text-blue-800'
        };
        return colors[estado] || 'bg-gray-100 text-gray-800';
    };

    const handleExportProperties = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/excel/export/properties'), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `propiedades_${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                alert('Error al exportar propiedades');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al exportar propiedades');
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/excel/template/properties'), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'plantilla_propiedades.xlsx';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                alert('Error al descargar plantilla');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al descargar plantilla');
        }
    };

    const handleImportProperties = async () => {
        if (!importFile) {
            alert('Por favor selecciona un archivo');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', importFile);

            const response = await fetch(apiUrl('/api/excel/import/properties'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const result = await response.json();

            if (response.ok) {
                setImportResults(result.data);
                await fetchProperties(1);
                setImportFile(null);
            } else {
                alert(result.message || 'Error al importar propiedades');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al importar propiedades');
        }
    };

    // Client-side filtering removed in favor of server-side search
    const filteredProperties = properties;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
                    <h2 className="text-2xl font-semibold text-gray-600">Gestión de Propiedades</h2>
                    <p className="text-gray-600 mt-2">Administra propiedades del hotel</p>
                </div>
                <div className="grid grid-cols-2 sm:flex sm:flex-row gap-3 sm:space-x-3">
                    <button
                        onClick={handleExportProperties}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2">
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        <span>Exportar</span>
                    </button>
                    <button
                        onClick={() => setShowImportModal(true)}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 flex items-center justify-center space-x-2">
                        <ArrowUpTrayIcon className="h-4 w-4" />
                        <span>Importar</span>
                    </button>
                    <button
                        onClick={handleDownloadTemplate}
                        className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center justify-center space-x-2">
                        <DocumentArrowDownIcon className="h-4 w-4" />
                        <span>Plantilla</span>
                    </button>
                    <button
                        onClick={() => {
                            setEditingProperty(null);
                            setFormData({
                                numero: '',
                                edificio: '',
                                tipo: 'habitacion',
                                descripcion: '',
                                capacidad: 1,
                                precioBase: 0,
                                piso: 1,
                                amenidades: [],
                                estado: 'disponible',
                                propietarios: []
                            });
                            setShowModal(true);
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
                    >
                        <PlusIcon className="h-4 w-4" />
                        <span>Nueva Propiedad</span>
                    </button>
                </div>
            </div>

            {/* Filtros y Búsqueda */}
            <div className="bg-white p-4 rounded-lg shadow-md border">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Buscar por número o descripción..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <FunnelIcon className="h-4 w-4 text-gray-400" />
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todos los tipos</option>
                            <option value="habitacion">Habitaciones</option>
                            <option value="apartamento">Apartamentos</option>
                            <option value="suite">Suites</option>
                        </select>
                        <select
                            value={filterEstado}
                            onChange={(e) => setFilterEstado(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todos los estados</option>
                            <option value="disponible">Disponibles</option>
                            <option value="ocupada">Ocupadas</option>
                            <option value="mantenimiento">Mantenimiento</option>
                            <option value="reservada">Reservadas</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Tabla de Propiedades */}
            <div className="bg-white rounded-lg shadow-md border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Propiedad
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Edificio
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Tipo
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Capacidad
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Precio
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Propietarios
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredProperties.map((property) => (
                                <tr key={property._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10">
                                                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                                                    <BuildingOffice2Icon className="h-5 w-5 text-green-600" />
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {property.numero}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    Piso {property.piso}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {property.edificio ? (
                                            <>
                                                <div className="text-sm text-gray-900">
                                                    {property.edificio.nombre}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {property.edificio.ciudad}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-sm text-red-600">
                                                Sin edificio asignado
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm text-gray-900">
                                            {getTipoLabel(property.tipo)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="flex items-center space-x-1">
                                            <UsersIcon className="h-4 w-4 text-gray-400" />
                                            <span>{property.capacidad} personas</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="flex items-center space-x-1">
                                            <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                                            <span>${property.precioBase.toLocaleString()}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEstadoColor(property.estado)}`}>
                                            {getEstadoLabel(property.estado)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="space-y-1">
                                            {property.propietarios.map((propietario, index) => (
                                                <div key={index} className="flex items-center space-x-1">
                                                    <span className="text-xs">
                                                        {propietario.usuario.nombre} {propietario.usuario.apellido}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        ({propietario.porcentajePropiedad}%)
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => handleEdit(property)}
                                                className="text-blue-600 hover:text-blue-900"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                            </button>
                                            <button className="text-green-600 hover:text-green-900">
                                                <EyeIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Paginación */}
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg shadow-md">
                <div className="flex flex-1 justify-between sm:hidden">
                    <button
                        onClick={() => fetchProperties(pagination.current - 1)}
                        disabled={pagination.current === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        Anterior
                    </button>
                    <button
                        onClick={() => fetchProperties(pagination.current + 1)}
                        disabled={pagination.current === pagination.pages}
                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        Siguiente
                    </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            Mostrando página <span className="font-medium">{pagination.current}</span> de{' '}
                            <span className="font-medium">{pagination.pages}</span> ({pagination.total} resultados)
                        </p>
                    </div>
                    <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                            <button
                                onClick={() => fetchProperties(pagination.current - 1)}
                                disabled={pagination.current === 1}
                                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:bg-gray-100"
                            >
                                <span className="sr-only">Anterior</span>
                                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                            </button>

                            {/* Números de página */}
                            {[...Array(pagination.pages)].map((_, i) => {
                                const pageNumber = i + 1;
                                // Mostrar primera, última, actual y adyacentes
                                if (
                                    pageNumber === 1 ||
                                    pageNumber === pagination.pages ||
                                    (pageNumber >= pagination.current - 1 && pageNumber <= pagination.current + 1)
                                ) {
                                    return (
                                        <button
                                            key={pageNumber}
                                            onClick={() => fetchProperties(pageNumber)}
                                            className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${pageNumber === pagination.current
                                                ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                                                : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                                }`}
                                        >
                                            {pageNumber}
                                        </button>
                                    );
                                } else if (
                                    pageNumber === pagination.current - 2 ||
                                    pageNumber === pagination.current + 2
                                ) {
                                    return (
                                        <span key={pageNumber} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                                            ...
                                        </span>
                                    );
                                }
                                return null;
                            })}

                            <button
                                onClick={() => fetchProperties(pagination.current + 1)}
                                disabled={pagination.current === pagination.pages}
                                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:bg-gray-100"
                            >
                                <span className="sr-only">Siguiente</span>
                                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">
                            {editingProperty ? 'Editar Propiedad' : 'Nueva Propiedad'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Edificio
                                </label>
                                <select
                                    required
                                    value={formData.edificio}
                                    onChange={(e) => setFormData({ ...formData, edificio: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">Seleccionar edificio</option>
                                    {buildings.map(building => (
                                        <option key={building._id} value={building._id}>
                                            {building.nombre} - {building.ciudad}
                                        </option>
                                    ))}
                                </select>
                                {editingProperty && !editingProperty.edificio && (
                                    <p className="text-sm text-red-600 mt-1">
                                        ⚠️ Esta propiedad no tiene edificio asignado. Debe seleccionar un edificio para continuar.
                                    </p>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Número de Propiedad
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.numero}
                                        onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tipo
                                    </label>
                                    <select
                                        required
                                        value={formData.tipo}
                                        onChange={(e) => setFormData({ ...formData, tipo: e.target.value as any })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="habitacion">Habitación</option>
                                        <option value="apartamento">Apartamento</option>
                                        <option value="suite">Suite</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Descripción
                                </label>
                                <textarea
                                    required
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Capacidad
                                    </label>
                                    <input
                                        type="number"
                                        required
                                        min="1"
                                        value={formData.capacidad}
                                        onChange={(e) => setFormData({ ...formData, capacidad: parseInt(e.target.value) })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Precio Base ($)
                                    </label>
                                    <input
                                        type="number"
                                        required
                                        min="0"
                                        step="0.01"
                                        value={formData.precioBase}
                                        onChange={(e) => setFormData({ ...formData, precioBase: parseFloat(e.target.value) })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Piso
                                    </label>
                                    <input
                                        type="number"
                                        required
                                        min="1"
                                        value={formData.piso}
                                        onChange={(e) => setFormData({ ...formData, piso: parseInt(e.target.value) })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Estado
                                </label>
                                <select
                                    required
                                    value={formData.estado}
                                    onChange={(e) => setFormData({ ...formData, estado: e.target.value as any })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="disponible">Disponible</option>
                                    <option value="ocupada">Ocupada</option>
                                    <option value="mantenimiento">Mantenimiento</option>
                                    <option value="reservada">Reservada</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Amenidades (separadas por comas)
                                </label>
                                <input
                                    type="text"
                                    value={formData.amenidades.join(', ')}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        amenidades: e.target.value.split(',').map(a => a.trim()).filter(a => a)
                                    })}
                                    placeholder="TV, WiFi, Aire acondicionado, Balcón"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Sección de Propietarios */}
                            <div>
                                <div className="flex justify-between items-center mb-3">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Propietarios
                                    </label>
                                    <button
                                        type="button"
                                        onClick={addPropietario}
                                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                    >
                                        + Agregar Propietario
                                    </button>
                                </div>

                                {formData.propietarios.map((propietario, index) => (
                                    <div key={index} className="flex items-center space-x-3 mb-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="flex-1">
                                            <select
                                                required
                                                value={propietario.usuario}
                                                onChange={(e) => updatePropietario(index, 'usuario', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="">Seleccionar usuario</option>
                                                {users.map(user => (
                                                    <option key={user._id} value={user._id}>
                                                        {user.nombre} {user.apellido} ({user.email})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="w-24">
                                            <input
                                                type="number"
                                                required
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                value={propietario.porcentajePropiedad}
                                                onChange={(e) => updatePropietario(index, 'porcentajePropiedad', parseFloat(e.target.value) || 0)}
                                                placeholder="%"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removePropietario(index)}
                                            className="text-red-600 hover:text-red-800 p-1"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}

                                {formData.propietarios.length > 0 && (
                                    <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm font-medium text-gray-700">
                                                Total de porcentajes:
                                            </span>
                                            <span className={`text-sm font-bold ${getTotalPorcentaje() === 100 ? 'text-green-600' : 'text-red-600'}`}>
                                                {getTotalPorcentaje().toFixed(1)}%
                                            </span>
                                        </div>
                                        {getTotalPorcentaje() !== 100 && (
                                            <p className="text-xs text-red-600 mt-1">
                                                Los porcentajes deben sumar exactamente 100%
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                >
                                    {editingProperty ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de Importación */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Importar Propiedades desde Excel</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Seleccionar archivo Excel
                                </label>
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="bg-blue-50 p-3 rounded-lg">
                                <p className="text-sm text-blue-800">
                                    <strong>Nota:</strong> Descarga la plantilla para ver el formato correcto.
                                    Las propiedades se actualizarán por número+edificio si ya existen, o se crearán nuevas si no existen.
                                    Asegúrate de que los edificios existan en el sistema.
                                </p>
                                <p className="text-sm text-blue-800 mt-2">
                                    <strong>Propietarios:</strong> Formato: email1:porcentaje1,email2:porcentaje2 (ej: juan@email.com:50,maria@email.com:50)
                                    Los porcentajes deben sumar 100%. Deja vacío si no hay propietarios.
                                </p>
                            </div>

                            {importResults && (
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <h3 className="font-medium text-gray-900 mb-2">Resultados de la importación:</h3>
                                    <p className="text-sm text-gray-600">
                                        ✅ {importResults.success} propiedades procesadas exitosamente
                                    </p>
                                    <p className="text-sm text-green-600">
                                        🆕 {importResults.created} propiedades creadas
                                    </p>
                                    <p className="text-sm text-blue-600">
                                        🔄 {importResults.updated} propiedades actualizadas
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        📊 Total de filas procesadas: {importResults.total}
                                    </p>
                                    {importResults.errors.length > 0 && (
                                        <div className="mt-2">
                                            <p className="text-sm text-red-600 font-medium">Errores encontrados:</p>
                                            <ul className="text-xs text-red-600 mt-1 max-h-20 overflow-y-auto">
                                                {importResults.errors.slice(0, 5).map((error: any, index: number) => (
                                                    <li key={index}>• Fila {error.row}: {error.error}</li>
                                                ))}
                                                {importResults.errors.length > 5 && (
                                                    <li>... y {importResults.errors.length - 5} errores más</li>
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowImportModal(false);
                                    setImportFile(null);
                                    setImportResults(null);
                                }}
                                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Cerrar
                            </button>
                            <button
                                onClick={handleImportProperties}
                                disabled={!importFile}
                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                Importar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertyManagement;

